type HTTPMethods = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH'

export interface PromiseWrapperOptions {
  contentType?: string
  withCredentials?: boolean
  noJson?: boolean
  headers?: Record<string, string>
}

export interface HttpError extends Error {
  response: {
    status: number
    statusText: string
    data: unknown
  }
}

export const createHttpError = (status: number, statusText: string, data: unknown): HttpError =>
  Object.assign(new Error(`Request failed with status code ${status}`), {
    name: 'HttpError',
    response: { status, statusText, data },
  })

export const isHttpError = (error: unknown): error is HttpError => error instanceof Error && error.name === 'HttpError'

export interface NetworkError extends Error {
  isNetworkError: true
}

export const createNetworkError = (): NetworkError =>
  Object.assign(new Error('Network error or CORS issue'), {
    name: 'NetworkError',
    isNetworkError: true as const,
  })

export const isNetworkError = (error: unknown): error is NetworkError =>
  error instanceof Error && error.name === 'NetworkError'

export const isHttpErrorStatus = (error: unknown, status: number): boolean =>
  isHttpError(error) && error.response.status === status

export const promiseWrapper = <T, P>(
  url: string,
  method: HTTPMethods,
  data?: P,
  options?: PromiseWrapperOptions,
  abortSignal?: AbortSignal,
): Promise<T> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)

    const onAbort = () => {
      xhr.abort()
      reject(new DOMException('Request cancelled', 'AbortError'))
    }
    abortSignal?.addEventListener('abort', onAbort, { once: true })
    const detachAbortListener = () => abortSignal?.removeEventListener('abort', onAbort)

    if (method === 'POST' || method === 'PUT') {
      xhr.setRequestHeader('Content-type', options?.contentType || 'application/json')
    }

    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value)
      })
    }

    if (options?.withCredentials) {
      xhr.withCredentials = true
    }

    xhr.onload = () => {
      detachAbortListener()

      let responseData = xhr.response

      //TODO: we should base parsing on this header, but first we need to correct the endpoints served from wp
      // const isJsonResponse = (xhr.getResponseHeader('Content-Type') ?? '').includes('application/json')
      //
      // if (isJsonResponse && xhr.response) {
      //   try {
      //     responseData = JSON.parse(xhr.response)
      //   } catch {
      //     // Content-Type claims JSON but body isn't valid JSON — keep the raw response.
      //   }
      // }

      // Until then: don't trust `Content-Type` — the cached `/wp-json/*` routes serve JSON as
      // `application/octet-stream` / `text/html`. Parse whatever parses, keep the rest raw.
      if (typeof responseData === 'string' && responseData) {
        try {
          responseData = JSON.parse(responseData)
        } catch {
          // Not JSON (or a malformed body) — keep the raw response.
        }
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(responseData)
      } else {
        reject(createHttpError(xhr.status, xhr.statusText, responseData))
      }
    }

    xhr.onerror = () => {
      detachAbortListener()
      reject(createNetworkError())
    }

    xhr.send(data ? (options?.noJson ? data.toString() : JSON.stringify(data)) : null)
  })
